import {
  authLinks,
  AuthRoutes,
  roleNames,
  stopImpersonation,
  useAuth,
  useImpersonation,
} from '@ox-technologies/auth';
import { useIsMobile } from '@ox-technologies/hooks';
import {
  AccountMenu,
  AccountMenuItem,
  AppHeader,
  AppHeaderLink,
  Button,
  ImpersonationBanner,
  LogoLOXLockup,
  Modal,
  Text,
} from '@ox-technologies/ui';
import { openExternalLink } from '@ox-technologies/utils';
import { useNavigate } from '@tanstack/react-router';
import ImpersonationStartModal from 'components/impersonation/impersonation-start-modal';
import {
  COMPANY_DETAIL_EXTERNAL_ROUTE,
  INSTALLATIONS_LIST_ROUTE,
} from 'config';
import { LogOut, UserCog } from 'lucide-react';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from 'utils/hooks/use-current-user';
import Breadcrumbs from 'utils/layout/breadcrumbs';
import { appRoutes } from 'utils/routing/routes';
import { MOBILE_BREAKPOINT } from 'utils/styles/breakpoints';
import { substituteRouteParams } from 'utils/substitute-route-params/substitute-route-params';

const ProtectedRoutesLayout = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { signoutRedirect } = useAuth();

  const isMobile = useIsMobile(MOBILE_BREAKPOINT);

  // Deliberately the signed-in admin, never the impersonated user: identity-web
  // is the account self-service app, so the profile and password pages (and the
  // API calls behind them) must stay yours. Impersonation here only changes the
  // dashboard's app directory and hands the target off to the app you open.
  const userData = useCurrentUser();
  const { isImpersonating, target, actor } = useImpersonation();

  const [confirmLogout, setConfirmLogout] = useState<boolean>(false);
  const [impersonationOpen, setImpersonationOpen] = useState<boolean>(false);

  const navLinks = [
    {
      label: t('NavBar.CompanyDetail'),
      onClick: () => {
        if (userData?.subjectId) {
          //TODO: take from @common/ui when it is ready
          openExternalLink(
            substituteRouteParams(COMPANY_DETAIL_EXTERNAL_ROUTE, {
              id: userData.subjectId,
            })
          );
        }
      },
    },
    {
      label: t('NavBar.InstallationsList'),
      onClick: () => openExternalLink(INSTALLATIONS_LIST_ROUTE),
    },
  ];

  const titles: Record<AuthRoutes, string> = {
    [AuthRoutes.CHANGE_PASSWORD]: t('Navbar.ChangePassword'),
    [AuthRoutes.MY_PROFILE]: t('Navbar.MyProfile'),
  };
  const authMenuLinks = authLinks.map((link) => ({
    label: titles[link.route],
    onClick: () => navigate({ to: link.link }),
  }));

  // Links are numbered sequentially across the header bar and the account
  // menu: 0..1 nav links, 2..3 auth links, 4 logout. On mobile the nav links
  // collapse into the account menu (the old navbar did the same below 768px).
  const menuItems: AccountMenuItem[] = [
    ...(isMobile
      ? navLinks.map<AccountMenuItem>((link, index) => ({
          ...link,
          testId: `navbar-menu-link-${index}`,
        }))
      : []),
    ...authMenuLinks.map<AccountMenuItem>((link, index) => ({
      ...link,
      testId: `navbar-menu-link-${navLinks.length + index}`,
    })),
    // Frontend-only gate for discoverability; the identity server is what
    // authorizes the exchange — it requires the dedicated ImpersonationAdmin
    // role in our own company's subject (`SubjectOptions.ContegSubjectId`,
    // identity-api#30), which GlobalAdmin does not grant. Gating on `isAdmin`
    // would offer the action to every global admin and let the server refuse
    // it. Hidden while impersonating — that session ends via the banner, and
    // chaining is refused anyway.
    ...(userData?.roleNames?.includes(roleNames.impersonationAdmin) &&
    !isImpersonating
      ? ([
          {
            label: t('Impersonation.Start.MenuItem'),
            icon: UserCog,
            onClick: () => setImpersonationOpen(true),
            testId: 'navbar-menu-impersonate',
          },
        ] satisfies AccountMenuItem[])
      : []),
    'divider',
    {
      label: t('Navbar.Logout'),
      icon: LogOut,
      variant: 'danger',
      onClick: () => setConfirmLogout(true),
      testId: `navbar-menu-link-${navLinks.length + authMenuLinks.length}`,
    },
  ];

  return (
    // App shell pinned to the viewport: the header stays fixed and only the
    // content region scrolls (body scroll would let the sticky header paint
    // over overlays).
    <div className="flex h-screen flex-col overflow-hidden bg-ox-canvas">
      {isImpersonating && target ? (
        <ImpersonationBanner
          targetName={target.fullName.trim() || target.email}
          targetEmail={target.email}
          actorName={actor?.fullName.trim() || actor?.email}
          onStop={stopImpersonation}
          testId="impersonation-banner"
        />
      ) : null}
      <AppHeader
        testId="navbar"
        logo={<LogoLOXLockup module="Identity" color="#fff" />}
        onLogoClick={() => navigate({ to: appRoutes.dashboard })}
        actions={
          <AccountMenu
            variant="dark"
            // fullName is `${FirstName} ${LastName}`, so a user without a
            // name gets " " — truthy, which used to win over the email and
            // leave the avatar without initials.
            name={userData?.fullName.trim() || userData?.email || ''}
            email={userData?.email || ''}
            // Avatar only on mobile: a long name (or the email standing in for
            // it) would push the trigger past the viewport edge. The popup
            // header still shows both.
            compact={isMobile}
            items={menuItems}
            testId="navbar-nav-toggle"
          />
        }
      >
        {!isMobile
          ? navLinks.map((link, index) => (
              <AppHeaderLink
                key={link.label}
                onClick={link.onClick}
                testId={`navbar-menu-link-${index}`}
              >
                {link.label}
              </AppHeaderLink>
            ))
          : null}
      </AppHeader>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto mt-10 flex w-[806px] max-w-full flex-col p-1 [&>div]:w-full max-md:min-h-[calc(100vh-96px)] max-md:[&_div:last-child]:flex-1 max-md:[&_h3]:hidden">
          {!isMobile ? <Breadcrumbs /> : null}
          {children}
        </div>

        {!isMobile ? (
          <div className="mt-10 w-full pb-4 text-center">
            <Text size="sm" className="text-ox-muted" data-testid="footer">
              {t('Footer.Text', { year: new Date().getFullYear() })}
            </Text>
          </div>
        ) : null}
      </div>

      <ImpersonationStartModal
        open={impersonationOpen}
        onClose={() => setImpersonationOpen(false)}
      />

      <Modal.Root
        open={confirmLogout}
        onOpenChange={(open) => !open && setConfirmLogout(false)}
      >
        <Modal.Portal>
          <Modal.Backdrop />
          <Modal.Popup data-testid="confirm-logout" className="max-w-md">
            <Modal.Header>
              <div>
                <Modal.Title>{t('LogOutModal.Title')}</Modal.Title>
                <Modal.Description className="mt-1">
                  {t('LogOutModal.Description')}
                </Modal.Description>
              </div>
              <Modal.Close />
            </Modal.Header>
            <Modal.Footer>
              <Button
                variant="secondary"
                testId="cancel-action-button"
                onClick={() => setConfirmLogout(false)}
              >
                {t('LogOutModal.Cancel')}
              </Button>
              <Button
                variant="primary"
                testId="confirm-action-button"
                onClick={() => signoutRedirect()}
              >
                {t('LogOutModal.Confirm')}
              </Button>
            </Modal.Footer>
          </Modal.Popup>
        </Modal.Portal>
      </Modal.Root>
    </div>
  );
};

export default ProtectedRoutesLayout;
